// src/components/vendor/pickup/PickupAddressSelection.tsx

import React, { useMemo } from 'react';
import { AlertCircle, Plus, MapPin, Phone } from 'lucide-react';
import type { Store } from '../../../types';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

interface StoreAddressRow {
    id: number;
    store: number;
    store_detail: { id: number; name: string };
    address: number;
    address_detail: any;
    is_default: boolean;
    is_active: boolean;
}

interface PickupAddressSelectionProps {
    storeAddresses: StoreAddressRow[];
    selectedStores: Store[];
    selectedAddressId: number | null;
    onSelect: (addressId: number) => void;
    onAddAddress: (storeId: number) => void;
    error?: string;
    loading?: boolean;
    // notes: string;
    // onNotesChange: (value: string) => void;
}

const PickupAddressSelection: React.FC<PickupAddressSelectionProps> = ({
    storeAddresses,
    selectedStores,
    selectedAddressId,
    onSelect,
    onAddAddress,
    error,
    loading = false,
    // notes,
    // onNotesChange,
}) => {
    // Dedupe by physical address ID, collect stores per address
    const groupedAddresses = useMemo(() => {
        const map = new Map<number, {
            address: any;
            storeAddressIds: number[];
            stores: { id: number; name: string }[];
        }>();

        storeAddresses.forEach(sa => {
            const key = sa.address_detail?.id ?? sa.address;
            if (!map.has(key)) {
                map.set(key, {
                    address: sa.address_detail,
                    storeAddressIds: [],
                    stores: [],
                });
            }
            const entry = map.get(key)!;
            entry.storeAddressIds.push(sa.id);
            if (sa.store_detail) entry.stores.push(sa.store_detail);
        });

        return Array.from(map.values());
    }, [storeAddresses]);

    const hasAnyAddress = groupedAddresses.length > 0;

    // Stores with no address registered
    const storesMissingAddress = useMemo(() => {
        return selectedStores.filter(store =>
            !storeAddresses.some(sa => sa.store === store.id)
        );
    }, [selectedStores, storeAddresses]);

    const formatAddress = (a: any) => {
        const parts = [
            a.house_number,
            a.address_line_1,
            a.address_line_2,
        ].filter(Boolean);
        return parts.join(', ');
    };

    const storeLabel = (stores: { id: number; name: string }[]) => {
        if (stores.length === 0) return '';
        if (stores.length === 1) return stores[0].name;
        return `${stores[0].name} +${stores.length - 1} other${stores.length > 2 ? 's' : ''}`;
    };

    const storeTooltip = (stores: { id: number; name: string }[]) =>
        stores.map(s => s.name).join(', ');

    const handleAddClick = () => {
        const storeId = storesMissingAddress[0]?.id ?? selectedStores[0]?.id;
        if (storeId) onAddAddress(storeId);
    };
    
    return (
        <div className="space-y-5">
            {/* Address list */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                        Pickup Address <span className="text-red-500">*</span>
                    </label>
                    <button
                        type="button"
                        onClick={handleAddClick}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Add Pickup Address
                    </button>
                </div>

                {loading ? (
                    <p className="text-sm text-gray-500">Loading addresses...</p>
                ) : groupedAddresses.length === 0 ? (
                    <p className="text-sm text-gray-500">No pickup addresses available.</p>
                ) : (
                    <div className="space-y-2 overflow-y-auto">
                        {groupedAddresses.map(({ address, storeAddressIds, stores }) => {
                            const isSelected = selectedAddressId !== null &&
                                storeAddressIds.includes(selectedAddressId);

                            return (
                                <label
                                    key={address.id}
                                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="pickupAddress"
                                        checked={isSelected}
                                        onChange={() => onSelect(storeAddressIds[0])}
                                        className="mt-0.5 h-4 w-4 text-blue-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-medium text-gray-900">
                                                {address.label || 'Address'}
                                            </p>
                                            {stores.length > 0 && (
                                                <span
                                                    className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
                                                    title={storeTooltip(stores)}
                                                >
                                                    {storeLabel(stores)}
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-sm text-gray-600 mt-0.5">
                                            {formatAddress(address)}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {address.city_details?.name || address.city}
                                        </p>

                                        {(address.contact_number_1 || address.contact_number_2) && (
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                                {address.contact_number_1 && (
                                                    <span className="flex items-center">
                                                        <Phone className="h-3 w-3 mr-1" />
                                                        {formatPhoneNumber(address.contact_number_1, { showFlag: false })}
                                                    </span>
                                                )}
                                                {address.contact_number_2 && (
                                                    <span className="flex items-center">
                                                        <Phone className="h-3 w-3 mr-1" />
                                                        {formatPhoneNumber(address.contact_number_2, { showFlag: false })}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {address.delivery_note && (
                                            <p className="text-xs text-gray-500 mt-1 italic">
                                                {address.delivery_note}
                                            </p>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}

                {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" /> {error}
                    </p>
                )}
            </div>

            {/* Stores missing address */}
            {storesMissingAddress.length > 0 && hasAnyAddress && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                        {storesMissingAddress.length === 1
                            ? `${storesMissingAddress[0].name} has no pickup address.`
                            : `${storesMissingAddress.length} stores have no pickup address.`}
                    </p>
                    <p className="text-sm text-blue-700 mt-0.5">
                        You can pick any of the addresses above — items will be collected from the same location.
                    </p>
                    {/* optional add link */}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {storesMissingAddress.map(store => (
                            <button
                                key={store.id}
                                type="button"
                                onClick={() => onAddAddress(store.id)}
                                className="text-xs text-blue-700 hover:text-blue-900 underline"
                            >
                                Add an address for {store.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {!hasAnyAddress && storesMissingAddress.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                    <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-amber-800">
                                No pickup addresses registered
                            </p>
                            <p className="text-sm text-amber-700 mt-0.5">
                                Add a pickup address to continue.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {storesMissingAddress.map(store => (
                            <button
                                key={store.id}
                                type="button"
                                onClick={() => onAddAddress(store.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                            >
                                <Plus className="h-3 w-3" /> Add for {store.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PickupAddressSelection;